import { z } from "zod";

export const recipientEntryTypeSchema = z.enum(["inp", "var"], {
  error: () => "Tipo de entrada inválido. Debe ser 'inp' o 'var'",
});

export const recipientEntrySchema = z.object({
  type: recipientEntryTypeSchema,
  value: z.string().min(1, "El valor es requerido"),
  // Campos para variables dinámicas (cuando type === "var")
  connection: z.string().optional(),
  field: z.string().optional(),
  output_name: z.string().optional(),
  global_type: z
    .enum([
      "APPLICANT",
      "NODE_EXECUTION_START",
      "NODE_EXECUTION_FINISH",
      "CURRENT_TIME",
    ])
    .optional(),
});

// Email-specific recipient entry schema with email validation
export const emailRecipientEntrySchema = z
  .object({
    type: recipientEntryTypeSchema,
    value: z.string().min(1, "El valor es requerido"),
    // Campos para variables dinámicas (cuando type === "var")
    connection: z.string().optional(),
    field: z.string().optional(),
    output_name: z.string().optional(),
    global_type: z
      .enum([
        "APPLICANT",
        "NODE_EXECUTION_START",
        "NODE_EXECUTION_FINISH",
        "CURRENT_TIME",
      ])
      .optional(),
  })
  .refine(
    (data) => {
      // Solo validar formato de email si es input directo (no variable)
      if (data.type === "inp" && data.value.trim() !== "") {
        return z.string().email().safeParse(data.value).success;
      }
      return true;
    },
    {
      path: ["value"],
      error: "El correo electrónico tiene un formato inválido",
    },
  );

export const variableEntrySchema = z.object({
  type: recipientEntryTypeSchema,
  value: z.string().min(1, "El valor es requerido"),
  var_type: z.enum(["STRING", "DATE", "FLOAT"]),
  // Campos para variables dinámicas (cuando type === "var")
  connection: z.string().optional(), // nodeId de origen
  field: z.string().optional(), // fieldId de la variable
  output_name: z.string().optional(),
  // Para variables globales (APPLICANT, NODE_EXECUTION_START, NODE_EXECUTION_FINISH)
  global_type: z
    .enum([
      "APPLICANT",
      "NODE_EXECUTION_START",
      "NODE_EXECUTION_FINISH",
      "CURRENT_TIME",
    ])
    .optional(),
});

export const notificationNodeSchema = z
  .object({
    template: z.string().optional(),
    title: z.string().max(500, "El título excede 500 caracteres").optional(),
    description: z
      .string()
      .max(2000, "La descripción excede 2000 caracteres")
      .optional(),
    attachments: z.array(z.string()).optional(),
    emails: z.array(emailRecipientEntrySchema),
    phones: z.array(recipientEntrySchema).optional(),
    variables: z.record(z.string(), z.array(variableEntrySchema)).optional(),
    required_variables: z.array(z.string()).optional(),
  })
  .refine(
    (data) => {
      const hasValidEmail = data.emails.some(
        (email) => email.value.trim() !== "",
      );
      return hasValidEmail;
    },
    {
      path: ["emails"],
      error: "Debe proporcionar al menos un correo de destino",
    },
  )
  .refine(
    (data) => {
      // Si no hay plantilla (o es "no-template"), el título es obligatorio
      if (!data.template || data.template === "no-template") {
        return data.title && data.title.trim().length > 0;
      }
      return true;
    },
    {
      path: ["title"],
      error: "El título es requerido cuando no se selecciona una plantilla",
    },
  )
  .refine(
    (data) => {
      // Si no hay plantilla (o es "no-template"), la descripción es obligatoria
      if (!data.template || data.template === "no-template") {
        return data.description && data.description.trim().length > 0;
      }
      return true;
    },
    {
      path: ["description"],
      error:
        "La descripción es requerida cuando no se selecciona una plantilla",
    },
  )
  .refine(
    (data) => {
      // Si hay plantilla y required_variables, verificar que todas las variables requeridas estén presentes
      if (
        data.template &&
        data.template !== "no-template" &&
        data.required_variables &&
        data.required_variables.length > 0
      ) {
        // Verificar que todas las variables requeridas tengan al menos un valor
        return data.required_variables.every((varName) => {
          const varEntries = data.variables?.[varName];
          return (
            varEntries &&
            varEntries.length > 0 &&
            varEntries.some((entry) => entry.value.trim() !== "")
          );
        });
      }
      return true;
    },
    {
      path: ["variables"],
      error: "Todas las variables requeridas deben tener al menos un valor",
    },
  );

// Tipos inferidos
export type RecipientEntryType = z.infer<typeof recipientEntryTypeSchema>;
export type RecipientEntry = z.infer<typeof recipientEntrySchema>;
export type VariableEntry = z.infer<typeof variableEntrySchema>;
export type NotificationNodeData = z.infer<typeof notificationNodeSchema>;
